/**
 * Cart upgrade offer (snippets/cart-upgrade-offer.liquid): 32GB -> 64GB storage upgrade.
 *
 * "Upgraden" adds the upgrade product for every 32GB device in the cart (/cart/add.js, line
 * property `_upgrade_for_variant_id` = the 32GB variant) and refreshes the cart page and drawer
 * through the theme's own `theme:cartchanged` event (Section Rendering API + merge), so the
 * page does not reload. The upgrade product has its own (upgrade) price; no discount is
 * involved. SP Utilities swaps the 32GB line and the upgrade line for the 64GB after the order.
 *
 * After every render it:
 * - applies the guard the snippet rendered (data-cart-upgrade-fix): an upgrade line without a
 *   32GB S1 in the cart is removed, a second line is removed and a quantity above the number
 *   of 32GB devices is cut back, with a short note; and
 * - records whether the cart was eligible for the offer, whether it was visible (A/B test
 *   variant) and whether it was added, as hidden cart attributes that end up on the order:
 *   _cart_upgrade_eligible, _cart_upgrade_shown, _cart_upgrade_added (the upgrade SKU).
 *
 * "Nee, dankjewel" closes the offer for the session (_cart_upgrade_dismissed records it).
 *
 * Offer events for SP Utilities (assets/offer-events.js, offer 'cart_upgrade'): 'shown' once
 * the card is really on screen, 'accept' after the upgrade is in the cart, 'dismiss' on
 * "Nee, dankjewel" and 'reopen' on the text button.
 */
(function () {
  if (window.__senifoneCartUpgradeInitialized) return;
  window.__senifoneCartUpgradeInitialized = true;

  var ATTRIBUTE_ELIGIBLE = '_cart_upgrade_eligible';
  var ATTRIBUTE_SHOWN = '_cart_upgrade_shown';
  var ATTRIBUTE_ADDED = '_cart_upgrade_added';
  var LINK_PROPERTY = '_upgrade_for_variant_id';
  var liveRegion = null;
  var syncTimer = null;
  var syncing = false;
  var attemptedFixes = {};

  function routeRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  function request(path, body) {
    var options = {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    };
    if (body) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return fetch(routeRoot() + path, options).then(function (response) {
      return response.json().catch(function () {
        return {};
      }).then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'HTTP ' + response.status);
          error.status = response.status;
          throw error;
        }
        return data;
      });
    });
  }

  function announce(message) {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    // Clear first so the same message is announced again on a second attempt.
    liveRegion.textContent = '';
    setTimeout(function () {
      liveRegion.textContent = message;
    }, 50);
  }

  // Offer events (assets/offer-events.js); measurement never breaks the card.
  function track(action, offer) {
    try {
      if (window.SenifoneOfferEvents) window.SenifoneOfferEvents.publish('cart_upgrade', action, offer);
    } catch (error) {
      // Measurement only.
    }
  }

  // The card is meant for this visitor: no A/B class, or the A/B class is on <html>.
  function isActive(offer) {
    var abClass = offer.dataset.cartUpgradeAb;
    return !abClass || document.documentElement.classList.contains(abClass);
  }

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
  }

  function isVisible(el) {
    return !!(el && el.offsetParent !== null);
  }

  // After the refresh the offer (and the focused button) is gone; move focus to the new line.
  function focusLine(lineKey) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      var selector = '[data-merge-list-item="item-key:' + lineKey + '"] .cart-item__description a.name';
      var target = null;
      document.querySelectorAll(selector).forEach(function (el) {
        if (!target && isVisible(el)) target = el;
      });
      if (target) {
        clearInterval(timer);
        if (!document.activeElement || document.activeElement === document.body) {
          target.focus();
        }
      } else if (attempts > 40) {
        clearInterval(timer);
      }
    }, 100);
  }

  function setBusy(offer, button, busy) {
    offer.dataset.busy = busy ? 'true' : 'false';
    offer.classList.toggle('is-loading', busy);
    button.disabled = busy;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  function upgrade(offer, button) {
    var variantId = parseInt(offer.dataset.variantId, 10);
    var quantity = parseInt(offer.dataset.quantity, 10);
    var sku = offer.dataset.sku;
    var errorEl = offer.querySelector('[data-cart-upgrade-error]');
    var properties = {};
    var lineKey = '';

    if (!variantId || !quantity) return;
    properties[LINK_PROPERTY] = String(offer.dataset.forVariantId || '');

    setBusy(offer, button, true);
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    announce(offer.dataset.updatingMessage);

    request('cart/add.js', { items: [{ id: variantId, quantity: quantity, properties: properties }] })
      .then(function (response) {
        if (response && response.items && response.items[0]) lineKey = response.items[0].key;
        var attributes = {};
        attributes[ATTRIBUTE_ADDED] = sku;
        return request('cart/update.js', { attributes: attributes }).catch(function (error) {
          // The upgrade is in the cart; only the measurement attribute is missing.
          console.warn('[Senifone upgrade] Could not record the add:', error);
        });
      })
      .then(function () {
        track('accept', offer);
        refreshCart();
        announce(offer.dataset.successMessage);
        if (lineKey) focusLine(lineKey);
      })
      .catch(function (error) {
        console.error('[Senifone upgrade] Upgrade failed:', error);
        setBusy(offer, button, false);
        if (errorEl) {
          errorEl.textContent = offer.dataset.errorMessage || '';
          errorEl.hidden = false;
        } else {
          announce(offer.dataset.errorMessage);
        }
      });
  }

  function showNote(wrapper, message) {
    if (!wrapper || !message) return;
    // Outside the wrapper: the cart refresh replaces the wrapper's content.
    var note = wrapper.previousElementSibling;
    if (!note || !note.hasAttribute('data-cart-upgrade-note')) {
      note = document.createElement('p');
      note.className = 'cart-upgrade-note';
      note.setAttribute('data-cart-upgrade-note', '');
      note.setAttribute('role', 'status');
      wrapper.parentNode.insertBefore(note, wrapper);
    }
    note.textContent = message;
  }

  // The guard: set upgrade lines that break the rules to the quantity the snippet asked for.
  function applyFixes() {
    var fixes = [];
    document.querySelectorAll('[data-cart-upgrade-fix]').forEach(function (el) {
      var id = el.dataset.lineKey;
      var quantity = parseInt(el.dataset.quantity, 10);
      var attempt = id + ':' + quantity;
      if (!id || isNaN(quantity) || attemptedFixes[attempt]) return;
      attemptedFixes[attempt] = true;
      fixes.push({ id: id, quantity: quantity, reason: el.dataset.reason, wrapper: el.closest('[data-merge="cart-upgrade-guard"]') });
    });
    if (!fixes.length) return Promise.resolve(false);

    return fixes
      .reduce(function (promise, fix) {
        return promise.then(function () {
          return request('cart/change.js', { id: fix.id, quantity: fix.quantity });
        });
      }, Promise.resolve())
      .catch(function (error) {
        console.warn('[Senifone upgrade] Guard could not update the cart:', error);
      })
      .then(function () {
        fixes.forEach(function (fix) {
          var message = fix.wrapper && fix.wrapper.getAttribute(fix.reason === 'removed' ? 'data-removed-message' : 'data-clamped-message');
          showNote(fix.wrapper, message);
          announce(message);
        });
        refreshCart();
        return true;
      });
  }

  // Applies the guard, then records the eligible/shown attributes for the rendered offer.
  function sync() {
    if (syncing) return;
    syncing = true;
    applyFixes().then(function (fixed) {
      syncing = false;
      // A fix re-renders the cart; the attributes are recorded after that render.
      if (!fixed) recordAttributes();
    });
  }

  function recordAttributes() {
    if (syncing) return;
    var eligible = '';
    var shown = '';
    document.querySelectorAll('[data-cart-upgrade-offer]').forEach(function (offer) {
      var sku = offer.dataset.sku;
      if (!sku) return;
      eligible = sku;
      if (isVisible(offer)) shown = sku;
    });
    if (!eligible) return;

    syncing = true;
    request('cart.js')
      .then(function (cart) {
        var current = cart.attributes || {};
        var attributes = {};
        var changed = false;
        if (current[ATTRIBUTE_ELIGIBLE] !== eligible) {
          attributes[ATTRIBUTE_ELIGIBLE] = eligible;
          changed = true;
        }
        if (shown && current[ATTRIBUTE_SHOWN] !== shown) {
          attributes[ATTRIBUTE_SHOWN] = shown;
          changed = true;
        }
        if (changed) return request('cart/update.js', { attributes: attributes });
        return null;
      })
      .catch(function (error) {
        console.warn('[Senifone upgrade] Cart sync failed:', error);
      })
      .then(function () {
        syncing = false;
      });
  }

  // The cart refresh has no "done" event; sync shortly after it settles.
  function scheduleSync(delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, delay);
  }

  // "Nee, dankjewel" closes the card to a small text button that opens it again. The closed
  // state is kept per browser session (sessionStorage; without storage only for this page), so
  // the card stays closed after a reload or a cart re-render. Closing is recorded as the cart
  // attribute _cart_upgrade_dismissed, next to the shown/added attributes.
  var collapsedInMemory = {};

  function isDismissed(key) {
    if (!key) return false;
    try {
      var stored = window.sessionStorage.getItem(key);
      if (stored !== null) return stored === '1';
    } catch (error) {
      // No storage (private mode, blocked): fall back to this page's memory.
    }
    return collapsedInMemory[key] === true;
  }

  function rememberDismissed(key, dismissed) {
    if (!key) return;
    collapsedInMemory[key] = dismissed;
    try {
      if (dismissed) {
        window.sessionStorage.setItem(key, '1');
      } else {
        window.sessionStorage.removeItem(key);
      }
    } catch (error) {
      // Kept in memory only.
    }
  }

  function restoreCollapsed() {
    document.querySelectorAll('[data-cart-upgrade-offer][data-dismiss-key]').forEach(function (offer) {
      offer.classList.toggle('is-collapsed', isDismissed(offer.dataset.dismissKey));
    });
  }

  function recordDismissed(sku) {
    if (!sku) return;
    request('cart.js')
      .then(function (cart) {
        var current = (cart.attributes || {})['_cart_upgrade_dismissed'] || '';
        var value = sku;
        if (value === current) return null;
        var attributes = {};
        attributes['_cart_upgrade_dismissed'] = value;
        return request('cart/update.js', { attributes: attributes });
      })
      .catch(function (error) {
        console.warn('[Cart upgrade] Could not record the dismiss:', error);
      });
  }

  function setCollapsed(offer, collapsed) {
    offer.classList.toggle('is-collapsed', collapsed);
    rememberDismissed(offer.dataset.dismissKey, collapsed);
    var target = collapsed
      ? offer.querySelector('[data-cart-upgrade-reopen]')
      : offer.querySelector('.cart-upgrade-offer__panel');
    if (target) target.focus();
    if (collapsed) recordDismissed(offer.dataset.sku);
    track(collapsed ? 'dismiss' : 'reopen', offer);
    if (!collapsed && window.SenifoneOfferEvents) window.SenifoneOfferEvents.check();
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-cart-upgrade-decline], [data-cart-upgrade-reopen]');
    if (!toggle) return;
    var offer = toggle.closest('[data-cart-upgrade-offer]');
    if (!offer) return;
    event.preventDefault();
    setCollapsed(offer, toggle.hasAttribute('data-cart-upgrade-decline'));
  });

  // A cart re-render replaces the card; close it again before it is painted.
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i += 1) {
        var nodes = records[i].addedNodes;
        for (var j = 0; j < nodes.length; j += 1) {
          var node = nodes[j];
          if (node.nodeType === 1 && (node.matches('[data-cart-upgrade-offer]') || node.querySelector('[data-cart-upgrade-offer]'))) {
            restoreCollapsed();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  restoreCollapsed();

  if (window.SenifoneOfferEvents) window.SenifoneOfferEvents.watch('cart_upgrade', '[data-cart-upgrade-offer]', isActive);

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-cart-upgrade-button]');
    if (!button) return;
    var offer = button.closest('[data-cart-upgrade-offer]');
    if (!offer || offer.dataset.busy === 'true') return;
    event.preventDefault();
    upgrade(offer, button);
  });

  document.addEventListener('theme:cartchanged', function () {
    scheduleSync(1500);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleSync(300);
    });
  } else {
    scheduleSync(300);
  }
})();
